import { useMemo, useCallback } from 'react';
import { useEventHandler } from './useEventHandler.js';

/**
 * Subscribe to a channel
 *
 * @param channelName The name of the channel you want to subscribe to.
 * @param active Indicates if the client should be activated
 * @typeparam Type of channel you're subscribing to.
 * @returns IUseChannel with Instance of the channel you just subscribed to and function to unsubscribe.
 *
 * @example
 * ```javascript
 * const { channel, unsubscribeChannel } = useChannel("my-channel")
 * unsubscribeChannel("my-channel");
 * ```
 */
function useChannel(channelName) {
  let active = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : true;
  const {
    client,
    subscribeChannel
  } = useEventHandler();
  const channel = useMemo(() => {
    if (!active) return;
    // Subscribes to new channel
    const newChannel = subscribeChannel === null || subscribeChannel === void 0 ? void 0 : subscribeChannel(channelName);
    return newChannel;
  }, [active, channelName, subscribeChannel]);
  const unsubscribeChannel = useCallback(channelName => {
    client === null || client === void 0 ? void 0 : client.unsubscribe(channelName);
  }, [client]);

  /** Return the channel for use. */
  return {
    channel,
    unsubscribeChannel
  };
}

export { useChannel };
//# sourceMappingURL=useChannel.js.map
